/**
 * AssetLoader - Utility for loading and managing dynamic assets
 * Scans the public/assets/ directory structure and loads metadata
 */

class AssetLoader {
  constructor() {
    this.assets = new Map();
    this.categories = new Map();
    this.loaded = false;
  }

  /**
   * Load all assets from the assets directory
   */
  async loadAssets() {
    try {
      // Load main metadata
      const mainMetadata = await this.loadMetadata('/assets/metadata.json');
      if (!mainMetadata) {
        console.warn('No main metadata found, using fallback structure');
        return this.loadFallbackAssets();
      }

      // Load each category
      for (const category of mainMetadata.categories) {
        await this.loadCategory(category);
      }

      this.loaded = true;
      console.log('Assets loaded successfully:', this.assets.size, 'assets in', this.categories.size, 'categories');
      return this.getAssetStructure();
    } catch (error) {
      console.error('Error loading assets:', error);
      return this.loadFallbackAssets();
    }
  }

  /**
   * Load a specific category
   */
  async loadCategory(category) {
    for (const subcategory of category.subcategories) {
      const metadataPath = `/assets/${subcategory.path}/metadata.json`;
      const metadata = await this.loadMetadata(metadataPath);
      
      if (metadata && metadata.assets) {
        const categoryKey = `${category.id}_${subcategory.id}`;
        this.categories.set(categoryKey, {
          id: categoryKey,
          title: metadata.displayName || subcategory.name,
          description: metadata.description,
          category: category.id,
          subcategory: subcategory.id,
          assets: []
        });

        // Process each asset
        for (const assetData of metadata.assets) {
          const asset = {
            id: assetData.filename.replace('.svg', ''),
            src: `/assets/${subcategory.path}/${assetData.filename}`,
            name: assetData.name,
            description: assetData.description,
            tags: assetData.tags || [],
            scaling: assetData.scaling || 50, // Default to 50% of canvas height
            type: assetData.type || 'asset',
            order: assetData.order || 999
          };

          this.assets.set(asset.id, asset);
          this.categories.get(categoryKey).assets.push(asset);
        }

        // Sort assets by order
        this.categories.get(categoryKey).assets.sort((a, b) => a.order - b.order);
      }
    }
  }

  /**
   * Load metadata from a JSON file
   */
  async loadMetadata(path) {
    try {
      const response = await fetch(path);
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      console.warn(`Failed to load metadata from ${path}:`, error);
      return null;
    }
  }

  /**
   * Fallback asset loading for when metadata is not available
   */
  loadFallbackAssets() {
    console.log('Loading fallback assets...');
    
    // This would be the current hardcoded structure
    // For now, return empty structure
    return {
      textBubbles: {
        title: "Text Bubbles",
        assets: [
          { id: "textBubble", type: "speechBubble", name: "Text Bubble", icon: "💬" }
        ]
      }
    };
  }

  /**
   * Get the asset structure in the format expected by AssetPanel
   */
  getAssetStructure() {
    const structure = {};

    // Add speech bubbles first (special case)
    structure.speechBubbles = {
      title: "Speech Bubbles",
      assets: [
        { id: "speechBubble", type: "speechBubble", name: "Speech Bubble", icon: "💬" }
      ]
    };
    
    for (const [categoryKey, category] of this.categories) {
      structure[categoryKey] = {
        title: category.title,
        assets: category.assets
      };
    }

    return structure;
  }

  /**
   * Get a specific asset by ID
   */
  getAsset(assetId) {
    return this.assets.get(assetId);
  }

  /**
   * Get all assets
   */
  getAllAssets() {
    return Array.from(this.assets.values());
  }

  /**
   * Get assets by category
   */
  getAssetsByCategory(categoryId) {
    return this.categories.get(categoryId)?.assets || [];
  }

  /**
   * Search assets by name or tags
   */
  searchAssets(query) {
    const results = [];
    const searchTerm = query.toLowerCase();

    for (const asset of this.assets.values()) {
      if (
        asset.name.toLowerCase().includes(searchTerm) ||
        asset.description?.toLowerCase().includes(searchTerm) ||
        asset.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      ) {
        results.push(asset);
      }
    }

    return results;
  }

  /**
   * Refresh assets (reload from server)
   */
  async refreshAssets() {
    this.assets.clear();
    this.categories.clear();
    this.loaded = false;
    return await this.loadAssets();
  }

  /**
   * Check if assets are loaded
   */
  isLoaded() {
    return this.loaded;
  }
}

// Create singleton instance
const assetLoader = new AssetLoader();

export default assetLoader;
